import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateOEE, calculatePlantOEE } from '@/utils/oeeCalculator';
import { toInputDate } from '@/utils/format';
import { useWorkCenters } from '@/hooks/useWorkCenters';

// Clock time of a date ("10:00 am"); estimated end = start + the stage's planned minutes
const timeOf = (v?: string | Date | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const estimatedEnd = (wo: any): Date | null =>
  wo.plannedEndAt ? new Date(wo.plannedEndAt)
    : wo.actualStartDate && wo.plannedTimeMinutes ? new Date(new Date(wo.actualStartDate).getTime() + wo.plannedTimeMinutes * 60000) : null;

interface OverviewDashboardProps {
  machines?: any[];
  workCenters?: any[];
  workOrders?: any[];
  productionOrders?: any[];
  productionEntries?: any[];
  downtimeEntries?: any[];
  operators?: any[];
  shifts?: any[];
}

const sameName = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function timeAgo(date: Date): string {
  const mins = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function dueLabel(plannedEnd?: string | null): { text: string; late: boolean } {
  if (!plannedEnd) return { text: '—', late: false };
  const end = new Date(plannedEnd);
  if (isNaN(end.getTime())) return { text: '—', late: false };
  end.setHours(23, 59, 59, 999);
  const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)}d overdue`, late: true };
  if (days <= 1) return { text: days === 0 ? 'Due today' : 'Due tomorrow', late: false };
  return { text: end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), late: false };
}

const machineStatusClass = (status: string) => {
  switch (status) {
    case 'RUNNING':
      return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    case 'MAINTENANCE':
      return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'BREAKDOWN':
      return 'text-rose-700 bg-rose-50 border-rose-200';
    default:
      return 'text-slate-600 bg-slate-50 border-slate-200';
  }
};

const oeeTextClass = (pct: number) => (pct >= 85 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-rose-600');

function Bar({ pct, className }: { pct: number; className: string }) {
  return (
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${className}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  );
}

function Panel({
  title,
  count,
  action,
  className = '',
  children,
}: {
  title: string;
  count?: number | string;
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl flex flex-col min-h-0 ${className}`}>
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100 shrink-0">
        <h2 className="text-xs font-bold text-[#1e293b] uppercase tracking-wide">
          {title}
          {count !== undefined && <span className="ml-1.5 text-[#94a3b8] font-semibold">({count})</span>}
        </h2>
        {action}
      </div>
      <div className="overflow-auto min-h-0 flex-1">{children}</div>
    </div>
  );
}

const Empty = ({ text }: { text: string }) => (
  <div className="px-4 py-6 text-center text-xs text-[#94a3b8]">{text}</div>
);

export default function OverviewDashboard(props: OverviewDashboardProps) {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const navigate = useNavigate();

  const machines = props.machines || [];
  const workCenters = props.workCenters || [];
  const workOrders = props.workOrders || [];
  const productionOrders = props.productionOrders || [];
  const productionEntries = props.productionEntries || [];
  const downtimeEntries = props.downtimeEntries || [];
  const operators = props.operators || [];
  const shifts = props.shifts || [];

  const today = toInputDate(new Date());
  const todayEntries = useMemo(() => productionEntries.filter((e) => e.date === today), [productionEntries, today]);

  const oeeData = useMemo(
    () => ({ shifts, productionEntries, downtimeEntries }),
    [shifts, productionEntries, downtimeEntries],
  );

  // ── Machines: status, current work order, today's output, today's OEE ──
  const machineRows = useMemo(() => {
    return machines.map((m) => {
      const wc = workCenters.find((w) => w.id === m.workCenterId);
      const entries = todayEntries.filter((e) => e.machineId === m.id);
      const activeWO = workOrders.find((wo) => wo.machineId === m.id && wo.status === 'IN_PROGRESS') || null;
      // An assigned work order that has not started holds the machine for its planned time
      const reservedWO = activeWO ? null
        : workOrders.find((wo) => wo.machineId === m.id && wo.status === 'PENDING' && wo.assignedAt) || null;
      const po = activeWO ? productionOrders.find((p) => p.id === activeWO.productionOrderId) : null;
      const openDowntime = downtimeEntries.find((d) => d.machineId === m.id && !d.isResolved) || null;
      return {
        ...m,
        workCenterName: wc?.name || m.workCenterName || '—',
        produced: entries.reduce((s, e) => s + e.producedQty, 0),
        rejected: entries.reduce((s, e) => s + e.rejectedQty, 0),
        oee: calculateOEE(m, today, 'all', oeeData),
        activeWO,
        reservedWO,
        product: po?.productName || activeWO?.itemName || '',
        openDowntime,
      };
    });
  }, [machines, workCenters, workOrders, productionOrders, downtimeEntries, todayEntries, oeeData, today]);

  // ── Summary ──
  const summary = useMemo(() => {
    const produced = todayEntries.reduce((s, e) => s + e.producedQty, 0);
    const rejected = todayEntries.reduce((s, e) => s + e.rejectedQty, 0);
    const total = produced + rejected;
    // Plant OEE from all of today's output (entries without a machine too), so it matches "Produced Today"
    const plant = calculatePlantOEE(machines, today, 'all', oeeData);
    return {
      running: machines.filter((m) => m.status === 'RUNNING').length,
      activeWOs: workOrders.filter((wo) => wo.status === 'IN_PROGRESS').length,
      qcPending: workOrders.filter((wo) => wo.status === 'QC_PENDING' || wo.status === 'COMPLETED_QC_PENDING').length,
      produced,
      rejected,
      rejectionRate: total > 0 ? (rejected / total) * 100 : 0,
      openDowntime: downtimeEntries.filter((d) => !d.isResolved).length,
      plantOee: plant.hasData ? plant.asPercent.oee : null,
      plantOeeNote: plant.plannedMinutes === 0 ? 'no shift planned today' : 'no output recorded yet',
    };
  }, [machines, workOrders, downtimeEntries, todayEntries, oeeData, today]);

  // ── Work in progress ──
  const activeWorkOrders = useMemo(() => {
    return workOrders
      .filter((wo) => ['IN_PROGRESS', 'QC_PENDING', 'COMPLETED_QC_PENDING', 'QC_FAILED'].includes(wo.status))
      .sort((a, b) => String(a.plannedEndDate).localeCompare(String(b.plannedEndDate)));
  }, [workOrders]);

  // ── Work center load ──
  const workCenterRows = useMemo(() => {
    return workCenters
      .map((wc) => {
        const wos = workOrders.filter((wo) => wo.workCenterId === wc.id || sameName(wo.workCenterName, wc.name));
        const open = wos.filter((wo) => wo.status !== 'COMPLETED');
        return {
          id: wc.id,
          name: wc.name,
          total: wos.length,
          completed: wos.length - open.length,
          active: wos.filter((wo) => wo.status === 'IN_PROGRESS').length,
          pending: wos.filter((wo) => wo.status === 'PENDING').length,
          pct: wos.length > 0 ? Math.round(((wos.length - open.length) / wos.length) * 100) : 0,
        };
      })
      .filter((wc) => wc.total > 0)
      .sort((a, b) => b.active - a.active || b.pending - a.pending);
  }, [workCenters, workOrders]);

  // ── Operator output today ──
  const operatorRows = useMemo(() => {
    const byOperator = new Map<string, { name: string; produced: number; rejected: number; minutes: number; entries: number }>();
    for (const e of todayEntries) {
      const key = e.operatorId || e.operatorName;
      if (!key) continue;
      const op = operators.find((o) => o.id === e.operatorId);
      const row = byOperator.get(key) || { name: op?.name || e.operatorName || '—', produced: 0, rejected: 0, minutes: 0, entries: 0 };
      row.produced += e.producedQty;
      row.rejected += e.rejectedQty;
      row.minutes += e.actualTimeMinutes;
      row.entries += 1;
      byOperator.set(key, row);
    }
    return [...byOperator.values()].sort((a, b) => b.produced - a.produced);
  }, [todayEntries, operators]);

  // ── Recent activity ──
  const activity = useMemo(() => {
    const items: { id: string; at: Date; text: string; icon: string; tone: string }[] = [];
    for (const e of productionEntries) {
      const at = new Date(e.createdAt);
      if (isNaN(at.getTime())) continue;
      items.push({
        id: `pe-${e.id}`,
        at,
        text: `${e.operatorName || 'Operator'} recorded ${formatQty(e.producedQty)} good${e.rejectedQty > 0 ? `, ${formatQty(e.rejectedQty)} rejected` : ''} on ${e.workOrderNumber || e.stageName}`,
        icon: e.rejectedQty > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line',
        tone: e.rejectedQty > 0 ? 'text-amber-600 bg-amber-50' : 'text-emerald-600 bg-emerald-50',
      });
    }
    for (const d of downtimeEntries) {
      const at = new Date(d.createdAt);
      if (isNaN(at.getTime())) continue;
      items.push({
        id: `dt-${d.id}`,
        at,
        text: `${d.machineName || 'Machine'} stopped — ${d.downtimeCodeName || d.category || 'downtime'}${d.isResolved ? ` (${d.durationMinutes} min, resolved)` : ' (open)'}`,
        icon: d.isResolved ? 'ri-tools-line' : 'ri-alarm-warning-line',
        tone: d.isResolved ? 'text-slate-600 bg-slate-100' : 'text-rose-600 bg-rose-50',
      });
    }
    for (const wo of workOrders) {
      if (wo.status !== 'COMPLETED' || !wo.actualEndDate) continue;
      const at = new Date(wo.actualEndDate);
      if (isNaN(at.getTime())) continue;
      items.push({
        id: `wo-${wo.id}`,
        at,
        text: `${wo.woNumber} (${wo.stageName}) completed`,
        icon: 'ri-flag-line',
        tone: 'text-indigo-600 bg-indigo-50',
      });
    }
    return items.sort((a, b) => b.at.getTime() - a.at.getTime()).slice(0, 15);
  }, [productionEntries, downtimeEntries, workOrders]);

  const kpis = [
    { label: 'Machines Running', value: `${summary.running}/${machines.length}`, icon: 'ri-settings-4-line', tone: 'text-emerald-600 bg-emerald-50' },
    { label: 'Work Orders In Progress', value: String(summary.activeWOs), sub: summary.qcPending > 0 ? `${summary.qcPending} waiting for QC` : undefined, icon: 'ri-play-circle-line', tone: 'text-indigo-600 bg-indigo-50' },
    { label: 'Produced Today', value: formatQty(summary.produced), sub: 'good units', icon: 'ri-stack-line', tone: 'text-sky-600 bg-sky-50' },
    { label: 'Rejected Today', value: formatQty(summary.rejected), sub: `${summary.rejectionRate.toFixed(1)}% of output`, icon: 'ri-close-circle-line', tone: summary.rejected > 0 ? 'text-rose-600 bg-rose-50' : 'text-slate-500 bg-slate-100' },
    { label: 'Open Downtime', value: String(summary.openDowntime), icon: 'ri-alarm-warning-line', tone: summary.openDowntime > 0 ? 'text-rose-600 bg-rose-50' : 'text-slate-500 bg-slate-100' },
    { label: 'Plant OEE Today', value: summary.plantOee === null ? '—' : `${summary.plantOee}%`, sub: summary.plantOee === null ? summary.plantOeeNote : undefined, icon: 'ri-speed-up-line', tone: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div className="space-y-3">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${k.tone}`}>
              <i className={`${k.icon} text-lg`} />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-[#64748b] font-medium truncate">{k.label}</div>
              <div className="text-lg font-bold text-[#1e293b] leading-tight">{k.value}</div>
              {k.sub && <div className="text-[10px] text-[#94a3b8] truncate">{k.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Machines + work in progress */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
        <Panel
          title="Machines"
          count={machineRows.length}
          className="xl:col-span-3 max-h-[340px]"
          action={
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => navigate('/manufacturing/shop-floor')}
                className="h-7 px-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-semibold cursor-pointer flex items-center gap-1"
              >
                <i className="ri-add-line" /> Production Entry
              </button>
              <button
                onClick={() => navigate('/manufacturing/downtime')}
                className="h-7 px-2.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-semibold cursor-pointer flex items-center gap-1"
              >
                <i className="ri-alarm-warning-line" /> Log Downtime
              </button>
            </div>
          }
        >
          {machineRows.length === 0 ? (
            <Empty text="No machines found for this warehouse" />
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[#64748b] text-[11px]">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Machine</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-left px-3 py-2 font-semibold">Current Work Order</th>
                  <th className="text-right px-3 py-2 font-semibold">Today</th>
                  <th className="text-right px-3 py-2 font-semibold">OEE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {machineRows.map((m) => {
                  const wo = m.activeWO;
                  const pct = wo && wo.plannedQty > 0 ? Math.round((wo.completedQty / wo.plannedQty) * 100) : 0;
                  return (
                    <tr key={m.id} className="hover:bg-slate-50/60">
                      <td className="px-3 py-2">
                        <div className="font-semibold text-[#1e293b]">{m.name}</div>
                        <div className="text-[11px] text-[#94a3b8]">{workCenterLookup.labelOf((m as any).workCenterId ?? (m as any).work_center_id, m.workCenterName)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${machineStatusClass(m.status)}`}>
                          {m.status}
                        </span>
                        {m.openDowntime && (
                          <div className="text-[10px] text-rose-600 mt-0.5 truncate max-w-[140px]">
                            {m.openDowntime.downtimeCodeName || 'Downtime open'}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 min-w-[180px]">
                        {wo ? (
                          <>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-[#1e293b] truncate">
                                {wo.woNumber} · {wo.stageName}
                              </span>
                              <span className="text-[11px] text-[#64748b] shrink-0">
                                {formatQty(wo.completedQty)}/{formatQty(wo.plannedQty)}
                              </span>
                            </div>
                            <Bar pct={pct} className="bg-indigo-500" />
                            {m.product && <div className="text-[10px] text-[#94a3b8] mt-0.5 truncate">{m.product}</div>}
                            <div className="text-[10px] text-[#64748b] mt-0.5 truncate">
                              {wo.operatorName || 'No operator'}
                              {wo.actualStartDate && ` · since ${timeOf(wo.actualStartDate)}`}
                              {estimatedEnd(wo) && ` · est. end ${timeOf(estimatedEnd(wo))}`}
                            </div>
                          </>
                        ) : m.reservedWO ? (
                          <>
                            <span className="font-medium text-blue-700 truncate block">Reserved · {m.reservedWO.woNumber} · {m.reservedWO.stageName}</span>
                            <div className="text-[10px] text-[#64748b] mt-0.5 truncate">
                              {m.reservedWO.operatorName || 'No operator'}
                              {m.reservedWO.plannedStartAt && ` · planned ${timeOf(m.reservedWO.plannedStartAt)}`}
                            </div>
                          </>
                        ) : (
                          <span className="text-[#94a3b8]">Free — no work order</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <span className="font-semibold text-[#1e293b]">{formatQty(m.produced)}</span>
                        {m.rejected > 0 && <span className="text-rose-600 ml-1">/ {formatQty(m.rejected)} rej</span>}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {m.oee.hasData ? (
                          <span className={`font-bold ${oeeTextClass(m.oee.asPercent.oee)}`}>{m.oee.asPercent.oee}%</span>
                        ) : (
                          <span className="text-[#94a3b8]" title="No output or downtime recorded today">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Work in Progress" count={activeWorkOrders.length} className="xl:col-span-2 max-h-[340px]">
          {activeWorkOrders.length === 0 ? (
            <Empty text="No work orders are running right now" />
          ) : (
            <div className="divide-y divide-slate-100">
              {activeWorkOrders.map((wo) => {
                const pct = wo.plannedQty > 0 ? Math.round((wo.completedQty / wo.plannedQty) * 100) : 0;
                const due = dueLabel(wo.plannedEndDate);
                const waitingQc = wo.status === 'QC_PENDING' || wo.status === 'COMPLETED_QC_PENDING';
                return (
                  <button
                    key={wo.id}
                    onClick={() => navigate(`/manufacturing/production-orders/${wo.productionOrderId}`)}
                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50/70 cursor-pointer"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-[#1e293b] truncate">
                        {wo.woNumber} · {wo.stageName}
                      </span>
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${
                          wo.status === 'QC_FAILED'
                            ? 'bg-rose-50 text-rose-700'
                            : waitingQc
                              ? 'bg-purple-50 text-purple-700'
                              : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {wo.status === 'QC_FAILED' ? 'QC FAILED' : waitingQc ? 'QC PENDING' : 'IN PROGRESS'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-[#64748b] mt-0.5 mb-1">
                      <span className="truncate">
                        {wo.productionOrderNumber}
                        {wo.operatorName ? ` · ${wo.operatorName}` : ''}
                        {wo.machineName ? ` · ${wo.machineName}` : ''}
                      </span>
                      <span className={`shrink-0 ml-2 ${due.late ? 'text-rose-600 font-semibold' : ''}`}>{due.text}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Bar pct={pct} className={wo.status === 'QC_FAILED' ? 'bg-rose-500' : 'bg-emerald-500'} />
                      <span className="text-[11px] font-semibold text-[#475569] shrink-0 w-20 text-right">
                        {formatQty(wo.completedQty)}/{formatQty(wo.plannedQty)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Work centers · operators · activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Panel title="Work Center Load" count={workCenterRows.length} className="max-h-[260px]">
          {workCenterRows.length === 0 ? (
            <Empty text="No work orders assigned to work centers" />
          ) : (
            <div className="divide-y divide-slate-100">
              {workCenterRows.map((wc) => (
                <div key={wc.id} className="px-3.5 py-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-[#1e293b] truncate">{wc.name}</span>
                    <span className="text-[11px] text-[#64748b] shrink-0 ml-2">
                      {wc.active} running · {wc.pending} waiting · {wc.completed}/{wc.total} done
                    </span>
                  </div>
                  <Bar pct={wc.pct} className="bg-indigo-500" />
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Operator Output Today" count={operatorRows.length} className="max-h-[260px]">
          {operatorRows.length === 0 ? (
            <Empty text="No production recorded today" />
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[#64748b] text-[11px]">
                <tr>
                  <th className="text-left px-3.5 py-2 font-semibold">Operator</th>
                  <th className="text-right px-3 py-2 font-semibold">Good</th>
                  <th className="text-right px-3 py-2 font-semibold">Rejected</th>
                  <th className="text-right px-3.5 py-2 font-semibold">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {operatorRows.map((op) => (
                  <tr key={op.name}>
                    <td className="px-3.5 py-2 font-medium text-[#1e293b]">{op.name}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">{formatQty(op.produced)}</td>
                    <td className={`px-3 py-2 text-right ${op.rejected > 0 ? 'text-rose-600 font-semibold' : 'text-[#94a3b8]'}`}>
                      {formatQty(op.rejected)}
                    </td>
                    <td className="px-3.5 py-2 text-right text-[#64748b]">{op.minutes} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Recent Activity" className="max-h-[260px]">
          {activity.length === 0 ? (
            <Empty text="No shop floor activity recorded yet" />
          ) : (
            <div className="divide-y divide-slate-100">
              {activity.map((a) => (
                <div key={a.id} className="px-3.5 py-2 flex items-start gap-2.5">
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${a.tone}`}>
                    <i className={`${a.icon} text-sm`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-[#1e293b] leading-snug">{a.text}</div>
                    <div className="text-[10px] text-[#94a3b8]">{timeAgo(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
